import React from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import { Dropdown, DropdownOption } from '@carsys/ui'
import { If, RedirectIf } from '@cbgms/base/utils/If'

export enum CheckResult {
  ShowOnMatch = 'show',
  HideOnMatch = 'hide'
}

export interface OuterProps<TEnv> {
  environments: TEnv[keyof TEnv] | TEnv[keyof TEnv][]
  result?: CheckResult
  ignoreSimulatedEnvironment?: boolean
}

export interface IEnvironmentFeature<TEnv> extends OuterProps<TEnv> {
  redirect?: boolean
  children: JSX.Element
}

export interface HookOptions {
  ignoreSimulatedEnvironment?: boolean
}

interface NewEnvironmentSuiteParams<TEnv> {
  useSimulatedEnvironment: () => TEnv[keyof TEnv] | null
  useSetSimulatedEnvironment: () => (env: TEnv[keyof TEnv]) => void
  useEnvironment: () => TEnv[keyof TEnv]
}

function NewEnvironmentSuite<TEnv extends object>(envEnum: TEnv, params: NewEnvironmentSuiteParams<TEnv>) {
  // build internal helpers
  const checkEnvironment = (needle: TEnv[keyof TEnv], haystack: TEnv[keyof TEnv] | TEnv[keyof TEnv][]) =>
    needle && Array.isArray(haystack) ? haystack.includes(needle) : needle === haystack

  const environmentChecker = (environment: TEnv[keyof TEnv], props: OuterProps<TEnv>) => {
    const status = props.result || CheckResult.ShowOnMatch
    const condition = checkEnvironment(environment, props.environments)

    return { condition: status === CheckResult.ShowOnMatch ? condition : !condition }
  }

  const useDetermineEnv = (ignoreSimulatedEnvironment?: boolean) => {
    const simulatedEnv = params.useSimulatedEnvironment()
    const regularENV = params.useEnvironment()

    let environment = simulatedEnv ?? regularENV
    if (ignoreSimulatedEnvironment) environment = regularENV
    return environment
  }

  // build exposed features
  const useHasEnvironmentFeature = (envs: TEnv[keyof TEnv] | TEnv[keyof TEnv][], options?: HookOptions) => {
    const checker = useHasEnvironmentFeatureFunc(options)
    return checker(envs)
  }
  const useHasEnvironmentFeatureFunc = (options?: HookOptions) => {
    const environment = useDetermineEnv(options?.ignoreSimulatedEnvironment)
    return (envs: TEnv[keyof TEnv] | TEnv[keyof TEnv][]) => checkEnvironment(environment, envs)
  }

  const EnvironmentFeature: React.FC<IEnvironmentFeature<TEnv>> = props => {
    const environment = useDetermineEnv(props.ignoreSimulatedEnvironment)
    const condition = environmentChecker(environment, props)
    const componentProps = { children: props.children, ...condition }
    return props.redirect ? RedirectIf(componentProps) : If(componentProps)
  }

  const EnvironmentSimulator = () => {
    const originalEnvironement = params.useEnvironment()
    const simulatedEnvironment = params.useSimulatedEnvironment()
    const setSimulatedEnvironment = params.useSetSimulatedEnvironment()

    const getMenuText = (envKey: string) => {
      let text = envKey
      if ((originalEnvironement as any) === getValueByKey(envKey)) text += ' (original)'
      return text
    }

    const getValueByKey = (envKey: string) => {
      return (envEnum as any)[envKey] as string
    }

    const handleSelection = (value: TEnv[keyof TEnv]) => {
      setSimulatedEnvironment(value)
    }

    return (
      <Box width='180px'>
        <FormControl fullWidth>
          <Dropdown
            label='Simulated environment'
            value={simulatedEnvironment || originalEnvironement}
            onChange={e => handleSelection(e.target.value as any as TEnv[keyof TEnv])}
          >
            {Object.keys(envEnum).map(env => (
              <DropdownOption key={env} value={getValueByKey(env)}>
                {getMenuText(env)}
              </DropdownOption>
            ))}
          </Dropdown>
        </FormControl>
      </Box>
    )
  }

  return {
    checkEnvironment,
    environmentChecker,
    useHasEnvironmentFeature,
    useHasEnvironmentFeatureFunc,
    EnvironmentFeature,
    EnvironmentSimulator
  }
}

export default NewEnvironmentSuite
